import React, { useCallback, useEffect, useRef, useState } from "react";
import { MDBBadge, MDBIcon, MDBSpinner } from "mdb-react-ui-kit";
import "./IncidentKanbanBoard.css";

const COLUMNS = [
  { status: "OPEN",          label: "Abiertas",           color: "primary",   icon: "circle-dot"  },
  { status: "IN_PROGRESS",   label: "En curso",           color: "warning",   icon: "spinner"     },
  { status: "WAITING_USER",  label: "Esp. respuesta",     color: "info",      icon: "clock"       },
  { status: "RESOLVED",      label: "Resueltas",          color: "success",   icon: "circle-check"},
  { status: "CLOSED",        label: "Cerradas",           color: "secondary", icon: "circle-xmark"},
];

const PRIORITY_COLOR = {
  LOW: "success",
  MEDIUM: "warning",
  HIGH: "danger",
  URGENT: "dark",
};

const LS_KEY = "inci_kanban_order";

function loadOrderFromStorage() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveOrderToStorage(order) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(order));
  } catch {
    /* silencio si localStorage no disponible */
  }
}

/**
 * Construye el orden de columnas a partir de la lista de incidencias de la API
 * y del orden guardado en localStorage.
 *
 * Regla de reconciliación:
 *  - IDs conocidos (en localStorage) se mantienen en su posición.
 *  - IDs nuevos (no en localStorage) se insertan al principio de su columna de estado,
 *    ordenados por Fecha_Apertura descendente (más recientes primero).
 *  - IDs en localStorage que ya no existen en la lista API se descartan.
 */
function buildColumnOrder(incidents, savedOrder) {
  const byStatus = {};
  COLUMNS.forEach((c) => (byStatus[c.status] = []));

  incidents.forEach((inc) => {
    const col = inc.estado;
    if (byStatus[col]) byStatus[col].push(inc.idIncidencia);
  });

  if (!savedOrder) {
    // Sin orden guardado: ordenar por fecha de apertura descendente dentro de cada columna
    const dateMap = {};
    incidents.forEach((inc) => (dateMap[inc.idIncidencia] = inc.fechaApertura || ""));
    const result = {};
    COLUMNS.forEach((c) => {
      result[c.status] = byStatus[c.status].sort(
        (a, b) => (dateMap[b] > dateMap[a] ? 1 : -1)
      );
    });
    return result;
  }

  // Combinar: mantener IDs del localStorage que siguen existiendo Y en esta columna, insertar nuevos al frente
  const currentStatusMap = {};
  incidents.forEach((inc) => (currentStatusMap[inc.idIncidencia] = inc.estado));
  const result = {};
  COLUMNS.forEach((c) => {
    // Solo conservar IDs cuyo estado actual (según API) coincide con esta columna
    const saved = (savedOrder[c.status] || []).filter(
      (id) => currentStatusMap[id] === c.status
    );
    const savedSet = new Set(saved);
    const newIds = byStatus[c.status].filter((id) => !savedSet.has(id));
    // Nuevos al principio
    result[c.status] = [...newIds, ...saved];
  });
  return result;
}

// ─── KanbanCard ─────────────────────────────────────────────────────────────

function KanbanCard({
  incident,
  isDragging,
  isOver,
  onDragStart,
  onDragEnd,
  onClick,
}) {
  return (
    <div
      className={`kanban-card ${isDragging ? "kanban-card--dragging" : ""} ${isOver ? "kanban-card--over" : ""}`}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
      title={`#${incident.idIncidencia} · ${incident.titulo}`}
    >
      <div className="kanban-card__header">
        <span className="kanban-card__id text-muted">#{incident.idIncidencia}</span>
        <MDBBadge color={PRIORITY_COLOR[incident.prioridad] || "secondary"} className="kanban-card__priority">
          {incident.prioridad}
        </MDBBadge>
      </div>
      <div className="kanban-card__title">{incident.titulo}</div>
      <div className="kanban-card__meta">
        <span className="kanban-card__app">{incident.appCode}</span>
        {(incident.creadorApodo || incident.creadorNombre) && (
          <span className="kanban-card__user">
            <MDBIcon fas icon="user" className="me-1" />
            {incident.creadorApodo || incident.creadorNombre}
          </span>
        )}
        {(incident.asignadoApodo) && (
          <span className="kanban-card__assigned">
            <MDBIcon fas icon="user-check" className="me-1" />
            {incident.asignadoApodo}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── IncidentKanbanBoard ─────────────────────────────────────────────────────

/**
 * Props:
 *   incidents      {array}   Lista de incidencias (de la API)
 *   isManager      {bool}    Habilita drag & drop
 *   loadingStatus  {bool}    Spinner de carga
 *   onStatusChange {func}    (id, newStatus) => Promise — llamado al mover entre columnas
 *   onCardClick    {func}    (id) => void — abrir modal de detalle
 */
export default function IncidentKanbanBoard({
  incidents = [],
  isManager = false,
  loadingStatus = false,
  onStatusChange,
  onCardClick,
}) {
  const [columnOrder, setColumnOrder] = useState(() =>
    buildColumnOrder(incidents, loadOrderFromStorage())
  );
  const [dragState, setDragState] = useState({
    draggedId: null,
    fromCol: null,
    overCol: null,
    overIndex: null,
  });
  const [statusChanging, setStatusChanging] = useState(false);

  // Actualizar orden cuando cambian las incidencias (refresh de la lista)
  useEffect(() => {
    setColumnOrder((prev) => {
      const next = buildColumnOrder(incidents, prev);
      saveOrderToStorage(next); // sincronizar localStorage con el estado real de la API
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidents]);

  // Mapa rápido id → incidencia
  const incidentMap = {};
  incidents.forEach((inc) => (incidentMap[inc.idIncidencia] = inc));

  // ── Drag handlers ──

  const handleDragStart = useCallback((e, id, fromCol) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(id));
    setDragState({ draggedId: id, fromCol, overCol: null, overIndex: null });
  }, []);

  const handleDragEnd = useCallback(() => {
    setDragState({ draggedId: null, fromCol: null, overCol: null, overIndex: null });
  }, []);

  const handleDragOver = useCallback(
    (e, toCol, overIndex) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setDragState((prev) =>
        prev.overCol === toCol && prev.overIndex === overIndex
          ? prev
          : { ...prev, overCol: toCol, overIndex: overIndex ?? null }
      );
    },
    []
  );

  const handleDragLeaveColumn = useCallback((e, col) => {
    // Solo limpiar si salimos del contenedor de la columna (no de un hijo)
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragState((prev) => (prev.overCol === col ? { ...prev, overCol: null, overIndex: null } : prev));
    }
  }, []);

  const handleDrop = useCallback(
    async (e, toCol, dropIndex) => {
      e.preventDefault();
      const { draggedId, fromCol } = dragState;
      if (draggedId == null) return;

      setDragState({ draggedId: null, fromCol: null, overCol: null, overIndex: null });

      if (fromCol === toCol) {
        // Reordenar dentro de la misma columna
        setColumnOrder((prev) => {
          const col = [...(prev[fromCol] || [])];
          const currentIdx = col.indexOf(draggedId);
          if (currentIdx === -1) return prev;
          col.splice(currentIdx, 1);
          const target = dropIndex != null ? Math.min(dropIndex, col.length) : col.length;
          col.splice(target, 0, draggedId);
          const next = { ...prev, [fromCol]: col };
          saveOrderToStorage(next);
          return next;
        });
        return;
      }

      // Mover a otra columna → cambiar estado en backend
      if (!onStatusChange) return;
      setStatusChanging(true);
      try {
        await onStatusChange(draggedId, toCol);
        // La UI se actualiza cuando el padre refresca `incidents`
      } finally {
        setStatusChanging(false);
      }
    },
    [dragState, onStatusChange]
  );

  // ── Render ──

  return (
    <div className="kanban-board">
      {(loadingStatus || statusChanging) && (
        <div className="kanban-loading-overlay">
          <MDBSpinner />
        </div>
      )}

      {COLUMNS.map((col) => {
        const ids = columnOrder[col.status] || [];
        const cards = ids.map((id) => incidentMap[id]).filter(Boolean);
        const isTargetCol = dragState.overCol === col.status;

        return (
          <div
            key={col.status}
            className={`kanban-column ${isTargetCol ? "kanban-column--over" : ""}`}
            onDragOver={(e) => handleDragOver(e, col.status, null)}
            onDragLeave={(e) => handleDragLeaveColumn(e, col.status)}
            onDrop={(e) => handleDrop(e, col.status, ids.length)}
          >
            {/* Cabecera */}
            <div className={`kanban-column__header kanban-column__header--${col.color}`}>
              <MDBIcon fas icon={col.icon} className="me-2" />
              <span className="kanban-column__label">{col.label}</span>
              <MDBBadge color={col.color} pill className="ms-auto">
                {cards.length}
              </MDBBadge>
            </div>

            {/* Tarjetas */}
            <div className="kanban-column__body">
              {cards.map((inc, idx) => (
                <React.Fragment key={inc.idIncidencia}>
                  {/* Zona de drop antes de esta tarjeta */}
                  {isManager && (
                    <div
                      className={`kanban-drop-zone ${
                        isTargetCol && dragState.overIndex === idx ? "kanban-drop-zone--active" : ""
                      }`}
                      onDragOver={(e) => handleDragOver(e, col.status, idx)}
                      onDrop={(e) => handleDrop(e, col.status, idx)}
                    />
                  )}
                  <KanbanCard
                    incident={inc}
                    isDragging={dragState.draggedId === inc.idIncidencia}
                    isOver={false}
                    onDragStart={(e) => handleDragStart(e, inc.idIncidencia, col.status)}
                    onDragEnd={handleDragEnd}
                    onClick={() => onCardClick?.(inc.idIncidencia)}
                  />
                </React.Fragment>
              ))}

              {/* Zona de drop al final de la columna */}
              {isManager && (
                <div
                  className={`kanban-drop-zone kanban-drop-zone--bottom ${
                    isTargetCol && dragState.overIndex === ids.length ? "kanban-drop-zone--active" : ""
                  }`}
                  onDragOver={(e) => handleDragOver(e, col.status, ids.length)}
                  onDrop={(e) => handleDrop(e, col.status, ids.length)}
                />
              )}

              {cards.length === 0 && (
                <div className="kanban-column__empty">Sin incidencias</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
